/**
 * Order Fulfillment — fulfillment domain operational semantics
 *
 * Derives fulfillment urgency from order state + elapsed process time.
 *
 * Risk is computed from process velocity — how long has an order been in
 * a given state without advancing? This measures throughput degradation,
 * not deadline violations. An order stalled in 'processing' for 3 days
 * is operationally dangerous regardless of any promised delivery date.
 *
 * This is the "time in state" model applied to fulfillment flows.
 *
 * Levels (priority order):
 *   stale-processing  — 'processing' ≥2 days, not yet shipped       → red
 *   shipment-overdue  — 'shipped' ≥7 days, no delivery confirmation  → red
 *   unconfirmed       — 'pending' ≥24h, no movement                  → amber
 *   processing        — 'processing', fresh                          → blue
 *   shipped           — 'shipped', normal window                     → blue
 *   pending           — 'pending', fresh                             → yellow
 *   confirmed         — confirmation received, queued for processing  → brand
 *   delivered         — resolved                                     → green
 *   cancelled         — resolved                                     → gray
 *
 * The `icon` field is a string name resolved by badge components via ICON_MAP.
 */

/**
 * @param {object} order — Order document from API
 * @returns {{ level, label, sub, elapsed, icon, rowBg, rowClass, badgeClass }}
 */
export function computeOrderFulfillment(order) {
  const now = new Date();
  const createdAt = new Date(order.createdAt);
  const hoursOld = (now - createdAt) / (1000 * 60 * 60);
  const daysOld = hoursOld / 24;

  const shippedAt = order.shippedAt ? new Date(order.shippedAt) : null;
  const daysShipped = shippedAt ? (now - shippedAt) / (1000 * 60 * 60 * 24) : null;

  const status = order.status || 'pending';

  // ─── Escalated states ──────────────────────────────────────────────
  if (status === 'processing' && daysOld >= 2) {
    const daysProcessing = Math.floor(daysOld);
    return {
      level: 'stale-processing',
      label: 'Processing',
      sub: `${daysProcessing}d — not shipped`,
      elapsed: daysProcessing,
      rowBg: 'bg-red-50 dark:bg-red-950/20',
      rowClass: 'border-l-4 border-red-500',
      badgeClass: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
      icon: 'AlertTriangle',
    };
  }

  if (status === 'shipped') {
    const elapsed = daysShipped !== null ? daysShipped : daysOld;
    if (elapsed >= 7) {
      const daysDelayed = Math.floor(elapsed);
      return {
        level: 'shipment-overdue',
        label: 'Shipped',
        sub: `${daysDelayed}d — no delivery`,
        elapsed: daysDelayed,
        rowBg: 'bg-red-50 dark:bg-red-950/20',
        rowClass: 'border-l-4 border-red-500',
        badgeClass: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
        icon: 'AlertTriangle',
      };
    }
  }

  if (status === 'pending' && hoursOld >= 24) {
    const displayAge = daysOld >= 1 ? `${Math.floor(daysOld)}d` : `${Math.floor(hoursOld)}h`;
    return {
      level: 'unconfirmed',
      label: 'Unconfirmed',
      sub: `${displayAge} — no movement`,
      elapsed: Math.floor(hoursOld),
      rowBg: 'bg-amber-50 dark:bg-amber-950/20',
      rowClass: 'border-l-4 border-amber-500',
      badgeClass: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
      icon: 'Clock',
    };
  }

  // ─── Active / nominal states ───────────────────────────────────────
  if (status === 'processing') {
    return {
      level: 'processing',
      label: 'In fulfillment',
      sub: null,
      elapsed: null,
      rowBg: '',
      rowClass: 'border-l-4 border-blue-400',
      badgeClass: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
      icon: 'Package',
    };
  }

  if (status === 'shipped') {
    const dDays = daysShipped !== null ? Math.floor(daysShipped) : null;
    return {
      level: 'shipped',
      label: 'Shipped',
      sub: dDays !== null ? `${dDays}d in transit` : null,
      elapsed: dDays,
      rowBg: '',
      rowClass: 'border-l-4 border-blue-400',
      badgeClass: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
      icon: 'Truck',
    };
  }

  if (status === 'pending') {
    const displayAge = daysOld >= 1 ? `${Math.floor(daysOld)}d ago` : `${Math.floor(hoursOld)}h ago`;
    return {
      level: 'pending',
      label: 'Awaiting confirmation',
      sub: displayAge,
      elapsed: null,
      rowBg: '',
      rowClass: '',
      badgeClass: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
      icon: 'Clock',
    };
  }

  if (status === 'confirmed') {
    return {
      level: 'confirmed',
      label: 'Confirmed',
      sub: null,
      elapsed: null,
      rowBg: '',
      rowClass: 'border-l-4 border-brand-400',
      badgeClass: 'bg-brand-100 text-brand-800 dark:bg-brand-900/40 dark:text-brand-300',
      icon: 'CheckCircle',
    };
  }

  if (status === 'delivered') {
    return {
      level: 'delivered',
      label: 'Delivered',
      sub: null,
      elapsed: null,
      rowBg: '',
      rowClass: '',
      badgeClass: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
      icon: 'CheckCircle',
    };
  }

  if (status === 'cancelled') {
    return {
      level: 'cancelled',
      label: 'Cancelled',
      sub: null,
      elapsed: null,
      rowBg: '',
      rowClass: '',
      badgeClass: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
      icon: 'XCircle',
    };
  }

  return {
    level: 'nominal',
    label: status,
    sub: null,
    elapsed: null,
    rowBg: '',
    rowClass: '',
    badgeClass: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    icon: null,
  };
}

/**
 * Sort weight by fulfillment urgency.
 *   orders.sort((a, b) =>
 *     ORDER_FULFILLMENT_SORT_ORDER[computeOrderFulfillment(a).level]
 *     - ORDER_FULFILLMENT_SORT_ORDER[computeOrderFulfillment(b).level]
 *   )
 */
export const ORDER_FULFILLMENT_SORT_ORDER = {
  'stale-processing': 0,
  'shipment-overdue': 1,
  'unconfirmed': 2,
  'processing': 3,
  'shipped': 4,
  'pending': 5,
  'confirmed': 6,
  'delivered': 7,
  'cancelled': 8,
};
